import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerPortal,
  DrawerTitle,
} from '@components/atoms/Drawer/Drawer';
import { Link } from '@components/atoms/Link/Link';
import { Spinner } from '@components/atoms/Spinner/Spinner';
import { useGSAP } from '@gsap/react';
import { IconBigPauseButton } from '@src/assets/icon-big-pause-button';
import { IconBigPlayButton } from '@src/assets/icon-big-play-button';
import { IconClose } from '@src/assets/icon-close';
import { IconDescription } from '@src/assets/icon-description';
import { IconPause } from '@src/assets/icon-pause';
import { IconPlay } from '@src/assets/icon-play';
import { IconShare } from '@src/assets/icon-share';
import { IconSound } from '@src/assets/icon-sound';
import { IconSoundOff } from '@src/assets/icon-sound-off';
import type { TvmShortInfosFragment } from '@src/lib/graphql/generated';
import { Image, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import gsap from 'gsap';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { create } from 'zustand';
import { SocialShare } from '../SocialShare/SocialShare';

gsap.registerPlugin(useGSAP);

export type ShortAuthor = TvmShortInfosFragment['author'];
export type ShortEntity = TvmShortInfosFragment['entity'];

/** Resolution of the progress slider — the input works in whole steps, the video in seconds. */
const PROGRESS_STEPS = 1000;

/** `HTMLMediaElement.HAVE_CURRENT_DATA` — the first frame is decoded and painted. */
const HAVE_CURRENT_DATA = 2;

type DrawerContentType = 'description' | 'share';

/** Marks the buttons that drive the drawer, so dismiss-on-outside-click can ignore them */
const drawerToggleAttribute = 'data-shorts-drawer-toggle';

/** vaul's own close animation, in ms — a panel swap waits this long before opening the next one */
const drawerCloseDuration = 500;

const entityLinkBase: Record<string, string> = {
  Movie: '/filme/',
  Show: '/serien/',
};

/** Reader-facing name for what the short is about, used as the panel's eyebrow */
const entityTypeLabel: Record<string, string> = {
  Movie: 'Film',
  Show: 'Serie',
  Vod: 'Stream',
};

/**
 * Movies and shows live on this portal, VODs point at the provider's own page.
 */
function getEntityLink(entity: ShortEntity) {
  if (!entity) return null;
  if (entity.__typename === 'Vod') return entity.url || null;

  const base = entityLinkBase[entity.__typename];
  return base ? `${base}${entity.slug || entity.id}` : null;
}

type Store = {
  isMuted: boolean;
  volume: number;
  toggleMuted: (isMuted?: boolean) => void;
};

const useShortsPlayerStore = create<Store>()((set) => ({
  isMuted: false,
  volume: 1,
  toggleMuted: (isMuted) =>
    set((state) => {
      if (isMuted !== undefined) {
        return { isMuted, volume: isMuted ? 0 : 1 };
      }
      return { isMuted: !state.isMuted, volume: state.isMuted ? 0 : 1 };
    }),
}));

interface Props extends PropsWithClassProps<'videoWrapper' | 'video' | 'placeholder' | 'drawerPortal'> {
  src: string;
  title: string;
  /** Mime type of the Fairu asset, e.g. `video/mp4` */
  mime?: string;
  description?: string;
  author?: ShortAuthor;
  entity?: ShortEntity;
  inView?: boolean;
  children?: React.ReactNode;
  isActive?: boolean;
  /** Reports the share/description drawer opening so the feed can suspend scroll snapping */
  onDrawerOpenChange?: (open: boolean) => void;
}

/**
 * Panel heading. The short is always *about* something, so the linked film or show carries the
 * title and the eyebrow names what kind of thing it is — the share panel has no such subject and
 * just names itself.
 */
function DrawerHeading({ content, title, entity }: Pick<Props, 'title' | 'entity'> & { content: DrawerContentType }) {
  const entityLink = getEntityLink(entity);
  const isExternalEntityLink = entity?.__typename === 'Vod';
  const eyebrow = content === 'description' ? entityTypeLabel[entity?.__typename] : null;
  const heading = content === 'share' ? 'Teilen' : entity?.title || title;
  const headingClasses = 'hyphenate font-herokid text-heading-mobile-3 leading-small md:text-heading-4';

  return (
    <div className="min-w-0">
      {eyebrow && (
        <span className="mb-2.5 flex items-center gap-2 text-label-base text-primary">
          <span className="h-px w-4 bg-primary" />
          {eyebrow}
        </span>
      )}
      {content === 'description' && entityLink ? (
        <Link
          href={entityLink}
          useNextLink={!isExternalEntityLink}
          target={isExternalEntityLink ? '_blank' : undefined}
          rel={isExternalEntityLink ? 'noreferrer' : undefined}
          className={twMerge(headingClasses, 'block underline-offset-4 transition-colors hover:text-primary')}
        >
          {heading}
        </Link>
      ) : (
        <span className={twMerge(headingClasses, 'block')}>{heading}</span>
      )}
    </div>
  );
}

function Description({
  title,
  author,
  entity,
  description,
  className,
}: Pick<Props, 'title' | 'description' | 'author' | 'entity'> & { className?: string }) {
  const hasAvatar = Boolean(author?.avatar?.url);

  return (
    <div
      className={twMerge(
        'tvm-scrollbar-dark mx-5 mb-6 overflow-y-auto overflow-x-hidden border-gray-600 border-t pt-5 pr-2',
        className,
      )}
    >
      {/* Only worth showing when the short is titled differently from the film it covers */}
      {title && title !== entity?.title && <p className="mb-4 text-gray-400 text-label-base">{title}</p>}
      {author?.name && (
        <div className="mb-5 flex shrink-0 items-center gap-3">
          <Image
            src={author.avatar?.url || '/assets/author-transparent.svg'}
            alt={author.avatar?.alt || `Autor ${author.name}`}
            copyright={author.avatar?.copyright_text || ''}
            // `imageMode` is what sets object-fit — the component writes it as an inline style,
            // so a utility class cannot do the job here
            imageMode={hasAvatar ? 'cover' : undefined}
            classProps={{
              container: twMerge(
                'size-10 shrink-0 overflow-hidden rounded-full bg-gray-600',
                // The placeholder is a bust that has to sit on the bottom edge; a real portrait
                // fills the circle instead
                !hasAvatar && 'flex items-end justify-center pt-1',
              ),
              root: hasAvatar ? 'size-full' : undefined,
            }}
            width={40}
            height={40}
          />
          <span className="flex min-w-0 flex-col">
            <span className="text-gray-400 text-label-sm">von</span>
            <span className="font-semibold text-base leading-small">{author.name}</span>
          </span>
        </div>
      )}
      {description && (
        <div className="text-base text-gray-300 leading-body" dangerouslySetInnerHTML={{ __html: description }} />
      )}
    </div>
  );
}

export function ShortsPlayer({
  classProps,
  src,
  mime,
  inView,
  children,
  title,
  description,
  author,
  entity,
  isActive,
  onDrawerOpenChange,
}: Props) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  /**
   * Sticky: a card that has been active once keeps its source, so scrolling back and forth does not
   * refetch the file. Cards that were never active never get one and stay on their poster frame.
   */
  const [shouldLoad, setShouldLoad] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<DrawerContentType>('description');
  const [drawerPortal, setDrawerPortal] = useState<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const { isMuted, toggleMuted } = useShortsPlayerStore();
  const animationContainer = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const drawerSwitchTimeout = useRef(null);

  const shareIconActiveColor = drawerOpen && drawerContent === 'share' ? '#FF0000' : undefined;
  const descriptionIconActiveColor = drawerOpen && drawerContent === 'description' ? '#FF0000' : undefined;

  /** The card is the one the feed has snapped to, so its video is the one that should be running */
  const shouldPlay = Boolean(inView && isActive);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    video.play().catch((error: DOMException) => {
      // A play cut short by the next load or pause reports `AbortError`; only a policy rejection
      // means the browser refused to start it with sound
      if (error?.name !== 'NotAllowedError') return;

      toggleMuted(true);
      video.muted = true;
      video.play().catch(() => undefined);
    });
  }, [toggleMuted]);

  const togglePlay = () => {
    const video = videoRef.current;
    // A card that never became active has no source, so there is nothing to toggle — the icons
    // would otherwise flip to "playing" over a still frame
    if (!video || !shouldLoad) return;

    if (video.paused) {
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  };

  const updateProgress = () => {
    const video = videoRef.current;
    if (!video?.duration) return;

    setProgress(video.currentTime / video.duration);
  };

  const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    const ratio = Number(event.target.value) / PROGRESS_STEPS;

    setProgress(ratio);
    if (video?.duration) {
      video.currentTime = ratio * video.duration;
    }
  };

  /**
   * Closes when the panel already showing is requested again. Switching between panels lets the
   * open one slide out first — vaul animates position, not height, so swapping the content in
   * place would snap the panel to the new height instead of easing into it.
   */
  const toggleDrawer = (content: DrawerContentType) => {
    clearTimeout(drawerSwitchTimeout.current);

    if (drawerOpen && drawerContent === content) {
      setDrawerOpen(false);
      return;
    }

    if (drawerOpen) {
      setDrawerOpen(false);
      drawerSwitchTimeout.current = setTimeout(() => {
        setDrawerContent(content);
        setDrawerOpen(true);
      }, drawerCloseDuration);
      return;
    }

    setDrawerContent(content);
    setDrawerOpen(true);
  };

  const openShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: 'TV-MEDIA Shorts',
          url: window.location.href || '',
        })
        .catch(() => undefined);
    } else {
      toggleDrawer('share');
    }
  };

  const openDescription = () => toggleDrawer('description');

  useGSAP(
    () => {
      gsap
        .timeline()
        .fromTo(
          '.play-button-animation',
          {
            opacity: 0,
            scale: 1,
          },
          {
            opacity: 1,
            scale: 1,
            duration: 0.3,
          },
        )
        .to('.play-button-animation', {
          opacity: 0,
          scale: 2.5,
          duration: 0.3,
        });
    },
    { dependencies: [isPlaying], scope: animationContainer },
  );

  useEffect(() => {
    /**
     * The server-rendered video starts loading before React hydrates, so
     * `loadeddata` / `play` can fire before the handlers below are attached.
     * Read the element once on mount to pick those up.
     */
    const video = videoRef.current;
    if (!video) return;

    if (video.readyState >= HAVE_CURRENT_DATA) setVideoLoaded(true);
    setIsPlaying(!video.paused);
    if (video.duration) setProgress(video.currentTime / video.duration);

    return () => clearTimeout(drawerSwitchTimeout.current);
  }, []);

  useEffect(() => {
    // Update volume when muted store state changes
    const video = videoRef.current;
    if (!video) return;

    video.muted = isMuted;
    video.volume = isMuted ? 0 : 1;
  }, [isMuted]);

  useEffect(() => {
    if (isActive) {
      containerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [isActive]);

  useEffect(() => {
    /**
     * The drawer mounts inside this card, which sits in the feed's mandatory scroll-snap
     * container. Firefox re-runs snap positioning on that mutation, loses the current snap
     * target and falls back to the first one — the feed jumps to the top and scrolls back.
     * Reported upwards so the feed can drop snapping for as long as a drawer is open.
     */
    if (!drawerOpen) return;

    onDrawerOpenChange?.(true);
    return () => onDrawerOpenChange?.(false);
  }, [drawerOpen, onDrawerOpenChange]);

  useEffect(() => {
    if (isActive) setShouldLoad(true);
  }, [isActive]);

  useEffect(() => {
    // Adding the `<source>` after mount does not start a fetch on its own — the element has to be
    // told to re-run its resource selection
    if (!shouldLoad) return;

    videoRef.current?.load();
  }, [shouldLoad]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Playing before the source is in place aborts as soon as the load above runs, and the element
    // is left paused on a video that did download
    if (shouldPlay && shouldLoad) {
      playVideo();
    } else {
      video.pause();
    }
  }, [shouldPlay, shouldLoad, playVideo]);

  return (
    <div
      className={twMerge(
        'relative mx-auto my-6 aspect-short max-h-[62vh] transform-gpu snap-start snap-always md:max-h-[74vh]',
        classProps?.root,
      )}
      ref={containerRef}
      style={{ pointerEvents: inView ? 'auto' : 'none' }}
    >
      <div className={twMerge('absolute size-full overflow-hidden rounded-xl', classProps?.videoWrapper)}>
        {/**
         * Sits behind the video and shows the short's first frame — asking a Fairu video URL for a
         * width answers with a still, which is what the image loader does to every source. Lazy
         * rather than the element's own `poster`, which all cards would fetch on mount.
         */}
        <Image
          src={src}
          alt=""
          copyright=""
          fill
          imageMode="cover"
          sizes="(min-width: 768px) 480px, 100vw"
          classProps={{ container: 'absolute inset-0 size-full' }}
        />
        {/* Tapping the video toggles playback — the same action is available as a button in the overlay */}
        <video
          ref={videoRef}
          className={twMerge('relative size-full cursor-pointer object-cover', classProps?.video)}
          playsInline
          loop
          // No video is fetched before its card is the active one — a feed of 15 shorts would
          // otherwise fire 15 requests on mount, and browsers pull the first frames along with the
          // metadata. Until then the frame behind stands in.
          preload={shouldLoad ? 'metadata' : 'none'}
          disablePictureInPicture
          onClick={togglePlay}
          onLoadedData={() => setVideoLoaded(true)}
          onCanPlay={(event) => {
            setVideoLoaded(true);
            // A play attempt made while the source was still being picked up is reported as an
            // abort and leaves the element paused — once it can play, start it for real
            if (shouldPlay && event.currentTarget.paused) playVideo();
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={updateProgress}
        >
          {shouldLoad && <source src={src} type={mime || 'video/mp4'} />}
          <track kind="captions" />
        </video>
        {/* Inside the rounded wrapper so the bar is clipped by the same corner radius as the video */}
        <input
          type="range"
          min={0}
          max={PROGRESS_STEPS}
          step={1}
          value={Math.round(progress * PROGRESS_STEPS)}
          onChange={seek}
          aria-label={`Fortschritt von ${title}`}
          className="shorts-progress absolute inset-x-0 bottom-0"
          style={{ '--shorts-progress': progress, display: !inView ? 'none' : undefined } as React.CSSProperties}
        />
      </div>
      <div
        ref={setDrawerPortal}
        className={twMerge('pointer-events-none relative size-full overflow-clip rounded-xl', classProps?.drawerPortal)}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-black/30 to-black/0" />
      <div
        className="absolute inset-x-0 top-0 flex items-center justify-between"
        style={{ display: !inView ? 'none' : undefined }}
      >
        <button type="button" onClick={togglePlay} className="p-5">
          {isPlaying ? (
            <IconPause classProps={{ root: 'size-4 drop-shadow-2xl' }} />
          ) : (
            <IconPlay classProps={{ root: 'size-4 drop-shadow-2xl' }} />
          )}
        </button>
        <button type="button" onClick={() => toggleMuted()} className="p-5">
          {isMuted ? (
            <IconSoundOff classProps={{ root: 'size-4 my-0.5 mr-1.5 drop-shadow-2xl' }} />
          ) : (
            <IconSound classProps={{ root: 'size-5 drop-shadow-2xl' }} />
          )}
        </button>
      </div>
      <div
        ref={animationContainer}
        className="pointer-events-none absolute inset-0 flex size-full items-center justify-center"
        style={{ display: !inView ? 'none' : undefined }}
      >
        <div className="play-button-animation">{isPlaying ? <IconBigPauseButton /> : <IconBigPlayButton />}</div>
      </div>
      {/**
       * The poster frame stands in for every card that is not loading, so this only covers the wait
       * for the active video — dimmed rather than opaque, since the frame behind it is the same
       * picture the video opens on.
       */}
      {shouldLoad && !videoLoaded && (
        <div className="absolute inset-0 z-10 size-full overflow-hidden rounded-xl">
          <div
            className={twMerge('z-50 flex size-full items-center justify-center bg-black/30', classProps?.placeholder)}
          >
            <Spinner iconSize="50px" />
          </div>
        </div>
      )}
      <div
        className="absolute right-4 bottom-6 flex flex-col gap-4 md:right-[-54px] md:bottom-0"
        style={{ display: !inView ? 'none' : undefined }}
      >
        <button
          type="button"
          className="button-icon"
          onClick={openShare}
          {...{ [drawerToggleAttribute]: '' }}
          style={{
            color: shareIconActiveColor,
            borderColor: shareIconActiveColor,
          }}
        >
          <IconShare fill={shareIconActiveColor} />
        </button>
        <button
          type="button"
          className="button-icon"
          onClick={openDescription}
          {...{ [drawerToggleAttribute]: '' }}
          style={{
            color: descriptionIconActiveColor,
            borderColor: descriptionIconActiveColor,
          }}
        >
          <IconDescription />
        </button>
      </div>
      {children}
      <Description title={title} description={description} author={author} entity={entity} className="hidden" />
      {/**
       * `noBodyStyles` keeps vaul from pinning `<body>` to `position: fixed` on Safari, which it
       * does to force the browser toolbar into view. That shrinks `100dvh`, reflows the snap feed
       * and pushes the player down before the drawer animates in. The drawer is portalled into the
       * player card and the page already locks body scrolling itself, so the body styles buy us
       * nothing here.
       */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} noBodyStyles>
        <DrawerPortal container={drawerPortal}>
          <DrawerContent
            /**
             * The card's width is derived from an aspect ratio over a `vh` height, so its edges
             * land on fractional pixels. Panel and backdrop are bled 1px sideways so rounding can
             * never leave a hairline of video showing; the portal's `overflow-clip` trims them
             * back to the card.
             */
            className="card-gradient -inset-x-px max-h-[75%] border-gray-600 border-t"
            overlayClassName="-inset-x-px bg-black/50"
            style={{ outline: 'none' }}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(event) => {
              // Without this the dismissable layer closes the drawer on the same click that
              // toggles it, and the button's handler immediately reopens it
              if ((event.target as HTMLElement)?.closest?.(`[${drawerToggleAttribute}]`)) {
                event.preventDefault();
              }
            }}
          >
            <DrawerHeader className="mx-5 flex items-start justify-between gap-4 px-0 pt-5 pb-5">
              <DrawerTitle asChild>
                <DrawerHeading content={drawerContent} title={title} entity={entity} />
              </DrawerTitle>
              <DrawerClose className="button-icon shrink-0" aria-label="Schließen">
                <IconClose />
              </DrawerClose>
            </DrawerHeader>
            {drawerContent === 'share' && (
              <SocialShare
                classProps={{
                  // Same header / rule / content rhythm as the description panel
                  root: 'px-5',
                  socialsWrapper: 'border-gray-600 border-t pt-5',
                  copyText: 'text-base',
                }}
              />
            )}
            {drawerContent === 'description' && (
              <Description title={title} description={description} author={author} entity={entity} />
            )}
          </DrawerContent>
        </DrawerPortal>
      </Drawer>
    </div>
  );
}
